import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsPhone } from '../lib/useIsPhone.js';
import { Button, EmptyState, Input, Spinner } from './ui.js';
import { SelectionActionBar } from './SelectionActionBar.js';
import { togglePageSelection } from '../lib/pageSelection.js';

/**
 * INFRA-007 — the Global UI Standard list shell.
 *
 * Every list screen in the product (inquiries, quotes, reminders, activity)
 * renders through this one component, which is what guarantees they all have
 * search, sort, bulk selection and pagination behaving identically (UI-004).
 *
 * Deliberately no drag handles or reorder affordance: reordering by drag is
 * excluded product-wide per the PRD, so the shell offers no hook for it.
 */
export interface Column<T> {
  key: string;
  header: string;
  /** Omit to make the column non-sortable. */
  sortKey?: string;
  render: (row: T) => ReactNode;
  align?: 'start' | 'end' | 'center';
  width?: string;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;

  loading?: boolean;
  onRowClick?: (row: T) => void;

  /**
   * Phone layout for one row. A table squeezed into 375px is a table either
   * way, so on the phone the screen drops the columns entirely and each row
   * draws itself: title, subtitle, status, actions. Screens that do not supply
   * one fall back to the stacked-table layout.
   */
  card?: (row: T) => ReactNode;

  /** Search box — omit `onSearchChange` to hide it. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  /** Arbitrary filter controls rendered in the toolbar. */
  filters?: ReactNode;
  /** Tab strip rendered above the toolbar. */
  tabs?: ReactNode;
  /** Buttons rendered at the toolbar's end (e.g. "New quote"). */
  toolbarActions?: ReactNode;

  sort?: string;
  dir?: 'asc' | 'desc';
  onSortChange?: (sort: string, dir: 'asc' | 'desc') => void;

  /** Bulk selection — omit `onSelectionChange` to disable checkboxes. */
  selection?: string[];
  onSelectionChange?: (ids: string[]) => void;
  bulkActions?: ReactNode;

  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;

  empty?: { title: string; body?: string; icon?: ReactNode; action?: ReactNode };
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  onRowClick,
  card,
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
  tabs,
  toolbarActions,
  sort,
  dir = 'desc',
  onSortChange,
  selection,
  onSelectionChange,
  bulkActions,
  page = 1,
  pageSize = 25,
  total = rows.length,
  onPageChange,
  empty,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const phone = useIsPhone();
  const asCards = Boolean(card) && phone;

  const selectable = Boolean(onSelectionChange);
  const selected = useMemo(() => new Set(selection ?? []), [selection]);
  const allIds = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  const toggleAll = () => onSelectionChange?.(togglePageSelection(selection ?? [], allIds));

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.([...next]);
  };

  const handleSort = (sortKey: string) => {
    if (!onSortChange) return;
    // Same column toggles direction; a new column starts descending.
    onSortChange(sortKey, sort === sortKey && dir === 'desc' ? 'asc' : 'desc');
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showToolbar = Boolean(onSearchChange || filters || toolbarActions);

  return (
    <section className="card">
      {tabs}

      {showToolbar && (
        <div className="toolbar">
          {onSearchChange && (
            <div className="toolbar__search">
              {/* Text, not `type="search"`: the search type's native Escape
                  empties the element without telling React, leaving a blank box
                  over a list that is still filtered. `inputMode` keeps the
                  phone keyboard's search key. */}
              <Input
                type="text"
                inputMode="search"
                value={search ?? ''}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder ?? t('common.search')}
                aria-label={searchPlaceholder ?? t('common.search')}
              />
            </div>
          )}
          {filters && <div className="toolbar__group">{filters}</div>}
          {toolbarActions && (
            <div className="toolbar__group toolbar__actions">{toolbarActions}</div>
          )}
        </div>
      )}

      {selectable && selected.size > 0 && (
        <SelectionActionBar count={selected.size} onClear={() => onSelectionChange?.([])}>
          {bulkActions}
          <Button variant="ghost" onClick={toggleAll}>{t('common.selectAll')}</Button>
        </SelectionActionBar>
      )}

      {loading ? (
        <div className="empty">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={empty?.icon}
          title={empty?.title ?? t('common.empty')}
          {...(empty?.body ? { body: empty.body } : {})}
          {...(empty?.action ? { action: empty.action } : {})}
        />
      ) : asCards ? (
        <ul className="card-list">
          {rows.map((row) => {
            const id = rowKey(row);
            return (
              <li key={id}>
                <article className="list-card" onClick={onRowClick ? () => onRowClick(row) : undefined}>
                  {selectable && (
                    <label className="list-card__selection" onClick={(event) => event.stopPropagation()}>
                      <input type="checkbox" className="checkbox" checked={selected.has(id)}
                        onChange={() => toggleOne(id)} aria-label={t('common.selectRow')} />
                    </label>
                  )}
                  {card!(row)}
                </article>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="table-wrap">
          <table className="table table--list">
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: '44px' }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label={t('common.selectAll')}
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    style={{ width: column.width, textAlign: column.align ?? 'start' }}
                    aria-sort={
                      sort && column.sortKey === sort
                        ? dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {column.sortKey && onSortChange ? (
                      <button className="th-sort" onClick={() => handleSort(column.sortKey!)}>
                        {column.header}
                        <span aria-hidden>
                          {sort === column.sortKey ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const id = rowKey(row);
                return (
                  <tr
                    key={id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {selectable && (
                      <td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selected.has(id)}
                          onChange={() => toggleOne(id)}
                          aria-label={t('common.selectRow')}
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      // The phone stacks each row into a card and hides the
                      // header row, so the cell has to carry its own column
                      // name — app.css reads this back through ::before.
                      <td
                        key={column.key}
                        data-label={column.header}
                        style={{ textAlign: column.align ?? 'start' }}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {onPageChange && total > pageSize && (
        <div className="pagination">
          <span>
            {t('common.page')} <span className="ltr-nums">{page}</span> {t('common.of')}{' '}
            <span className="ltr-nums">{totalPages}</span> · <span className="ltr-nums">{total}</span>{' '}
            {t('common.results')}
          </span>
          <span className="pagination__spacer" />
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            {t('common.previous')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </section>
  );
}
